import { ChangeEvent, useState } from "react";
import { FileEditAltIcon, TextInput, ToggleInput } from "@datagame/ui";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { GAME_ALL } from "../../../../../../../graphql/queries/GameQueries";
import { QuestionsSection } from "./QuestionsSection";
import { Section } from "../../Common";
import {
  TRIVIA_CONFIGURATION_UPDATE,
  LEADERBOARD_CONFIGURATION_UPDATE,
} from "../../../../../../../graphql/mutations/GameMutation";
import { TriviaGame } from "../../../../../../../graphql/helpers/isTriviaGame";
import { useProject } from "../../../../../../../contexts/project/ProjectContext";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";
import { isLeaderboardGame } from "../../../../../../../graphql/helpers/LeaderboardGame";
import { Accordion } from "../../../../../../shared/ui/Accordion";
import { LeaderboardConfigSection } from "../LeaderboardConfigSection";
import { LeaderboardConfigurationUpdateInput } from "../../../../../../../gql/graphql";

import styles from "./TriviaSettingsForm.module.scss";

export interface TriviaSettingsFormProps {
  game: TriviaGame;
}

export const TriviaSettingsForm = ({ game }: TriviaSettingsFormProps) => {
  const { t } = useTranslation("trivia");
  const [timeLimit, setTimeLimit] = useState<number>(game.configuration.timeToCompleteInSeconds);
  const { project: { id: projectId } = {} } = useProject();

  const [showLeaderboard, setShowLeaderboard] = useState(
    isLeaderboardGame(game) ? game.configuration.showLeaderboard : false,
  );

  const [updateConfiguration] = useMutation(TRIVIA_CONFIGURATION_UPDATE, {
    refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
  });
  const [updateLeaderboardConfig] = useMutation(LEADERBOARD_CONFIGURATION_UPDATE);

  const handleSave = () => {
    updateConfiguration({
      variables: {
        triviaConfigurationId: game.configuration.id,
        data: {
          timeToCompleteInSeconds: timeLimit,
        },
      },
    });
  };

  const handleShowLeaderboardChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    setShowLeaderboard(checked);

    updateConfiguration({
      variables: {
        triviaConfigurationId: game?.configuration.id,
        data: {
          showLeaderboard: checked,
        },
      },
    });
  };

  const handleLeaderboardConfigurationChange = (data: LeaderboardConfigurationUpdateInput) => {
    if (projectId) {
      updateLeaderboardConfig({
        variables: {
          leaderboardConfigurationId: game?.configuration.leaderboardConfiguration?.id,
          data,
        },
        refetchQueries: [{ query: GAME_ALL, variables: { projectId } }],
      });
    }
  };

  return (
    <>
      <QuestionsSection game={game} />
      <Section
        title={t("gameSettings.timeSection")}
        tooltip={<TooltipWithIcon content={t("gameSettings.timeTooltip")} />}
      >
        <TextInput
          icon={<FileEditAltIcon />}
          size="small"
          type="number"
          value={timeLimit}
          onBlur={handleSave}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setTimeLimit(parseInt(e.target.value))}
        />
      </Section>

      <Section title={t("project:gameSettings.form.preferences")}>
        <ToggleInput
          className={styles.toggleWrapper}
          value={showLeaderboard}
          name="show-leaderboard"
          label="Show leaderboard"
          onChange={handleShowLeaderboardChange}
          tooltip={<TooltipWithIcon content={t("project:gameSettings.form.showLeaderboardTooltip")} />}
        />

        {showLeaderboard && (
          <Accordion title={t("project:gameSettings.form.leaderboardConfig")}>
            <LeaderboardConfigSection
              leaderboardConfiguration={game.configuration.leaderboardConfiguration}
              onUpdate={handleLeaderboardConfigurationChange}
            />
          </Accordion>
        )}
      </Section>
    </>
  );
};
